"use client";

import { useEffect, useState, useRef, Suspense } from "react";
import { useRouter, useSearchParams } from "next/navigation";
import { getSession, clearSession } from "@/lib/auth";
import { api } from "@/lib/api";
import type { PricingEstimate, Job } from "@/lib/types";
import { formatEstimateRange } from "@/lib/pricing";
import { motion, AnimatePresence } from "framer-motion";
import { MediaUploader } from "@/components/ui/MediaUploader";
import { JobDetails } from "@/components/JobDetails";
import { 
  ChevronRight, 
  MapPin, 
  Clock, 
  Sparkles, 
  ArrowLeft, 
  ShieldCheck, 
  Info,
  CheckCircle2,
  Phone,
  Droplet, 
  Zap, 
  Layers, 
  Paintbrush, 
  Wrench, 
  Sparkles as SparklingIcon, 
  Sprout, 
  Grid, 
  Snowflake, 
  Hammer,
  HelpCircle,
  Search
} from "lucide-react";
import Link from "next/link";
import dynamic from "next/dynamic";

const DestinationPicker = dynamic(() => import("@/components/DestinationPicker").then(mod => mod.DestinationPicker), { 
  ssr: false,
  loading: () => <div className="h-[400px] bg-slate-50 rounded-3xl animate-pulse" />
});

const URGENCY_OPTIONS = [
  { value: "urgent now", label: "Urgent", sub: "Within 2 hours" },
  { value: "today", label: "Today", sub: "Same day" },
  { value: "flexible", label: "Flexible", sub: "Next few days" },
];

const HIPAGES_SERVICE_SLUGS = [
  "air_conditioner_cleaning", "air_conditioning", "air_conditioning_repairs_and_maintenance", "all",
  "antenna_services", "appliance_installation", "appliance_repairs", "arborists", "architects",
  "architectural_town_planning_services", "asbestos_removal", "awning_suppliers", "awnings",
  "balustrades", "bamboo_flooring", "bath_resurfacing", "bathroom_accessories", "bathroom_renovations",
  "bathroom_stripout", "blinds", "bricklayers", "builders", "building_certifiers", "building_consultants",
  "building_designers", "building_suppliers", "building_surveyors", "cabinet_makers", "carpenters",
  "carpet_cleaning", "carpet_layers", "carpets", "carports", "ceilings", "chimney_sweepers", "cladding",
  "cleaning_services", "commercial_cleaning", "concrete_driveways", "concrete_kerbs", "concrete_resurfacing",
  "concretors", "construction_project_management", "curtains", "custom_furniture", "damp_proofing",
  "decking", "decking_repairs", "deep_cleaning", "demolition", "dishwasher_appliance_repairs",
  "door_repairs", "door_suppliers", "doors", "drafters", "electricians", "equipment_hire",
  "ev_charger_installation", "excavation", "fence_repairs", "fences", "fences_general_contractor",
  "fences_timber", "floor_coatings", "floor_sanding", "flyscreen_repairs", "flyscreens",
  "foxtel_and_tv_and_satellite_antenna_services", "funiture_chair", "furniture_removal",
  "furniture_removal_small_move", "garage_door_repairs", "garages", "garden_designer", "garden_maintenance",
  "garden_maintenance_garden_cleanup", "gas_fitters", "gates", "gates_automatic_gates", "gazebo",
  "glass_glazier", "glass_glazier_glass_cutting", "granny_flats", "gutter_cleaning", "gutter_replacement",
  "guttering", "guttering_repairs", "handrails", "heaters", "heating_systems",
  "heating_systems_underfloor_heating", "home_automation", "home_renovations", "home_theatre", "homewares",
  "hot_water_systems", "hybrid_flooring", "inspection_services", "inspections_prepurchase_property_inspections",
  "insulation", "interior_decorator", "interior_designer", "interior_designer_kitchen_design",
  "irrigation_systems", "irrigation_systems_irrigation_repairs", "joiners", "kitchen_benchtops",
  "kitchen_cabinet_makers", "kitchen_renovations", "kitchen_resurfacing", "landscape_architect",
  "landscaper_gardener", "lawn_mowing", "lawn_turf", "lawn_turf_synthetic_turf_supply_and_install",
  "leak_detection", "lighting", "locksmiths", "locksmiths_key_cutting", "mattress_cleaning",
  "oven_and_range_hood_repairs", "painters", "patios", "patios_repairs", "paver", "pergola",
  "pest_control", "pest_control_possums", "pest_inspections", "plaster_repairs", "plastering", "plumbers",
  "pool_fencing", "pool_heating", "pool_maintenance", "pool_maintenance_pool_cleaning_service", "pools",
  "pressure_cleaner", "privacy_screens", "professional_organisers", "refrigerator_repairs", "render",
  "renderer", "rental_bond_cleaning", "retaining_walls", "roller_shutters", "roof_insulation",
  "roof_repairs", "roofing", "rubbish_remover", "rubbish_remover_skip_and_bin_hire", "scaffolding",
  "security", "security_screen_door_repairs", "security_screens_doors", "shades_sails", "sheds",
  "sheds_shed_builders", "shop_fitters", "shower_screens", "skip_truck_hire", "skylights", "soil_testing",
  "solar_panel_cleaners", "solar_power", "splashbacks", "stained_glass", "staircases_handrails",
  "stonemasons", "storage", "structural_engineer", "supply_and_install_gates", "surveyor", "tilers",
  "timber_flooring", "timber_flooring_timber_floor_repairs", "tree_fellers", "underpinning",
  "upholstery_repair", "ventilation", "verandahs", "vinyl_laminate", "wallpaperers", "wardrobes",
  "wardrobes_built_in_wardrobe_builders", "washing_machine_repairs", "waterproofers", "window_cleaners",
  "window_repairs", "window_shutters", "window_tinting", "windows", "wire_fencing"
];

function formatServiceLabel(slug: string) {
  return slug
    .split("_")
    .map((word) => word.length <= 2 ? word.toUpperCase() : word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}

// These hipages slugs duplicate one of the curated categories below (e.g.
// "plumbers" vs "plumbing"). Keeping both meant a search for "plumber" could
// surface the thin auto-generated card instead of the curated one, and
// picking it sent a category id ("plumbers") that neither CATEGORY_QUESTIONS
// nor the backend's CATEGORY_ALIASES fully recognise as the same trade —
// so drop the duplicates and let the curated card's keywords cover the search term.
const CURATED_DUPLICATE_SLUGS = new Set([
  "plumbers", "electricians", "painters", "tilers", "cleaning_services",
]);

const HIPAGES_CATEGORY_METADATA = HIPAGES_SERVICE_SLUGS
  .filter((slug) => !CURATED_DUPLICATE_SLUGS.has(slug))
  .map((slug) => {
    const label = formatServiceLabel(slug);
    return { id: slug, label, icon: Wrench, description: `${label} service` };
  });

const CATEGORY_METADATA = [
  { id: "plumbing", label: "Plumbing", icon: Droplet, description: "Leaky pipes, taps, toilets & hot water", keywords: "plumber plumbers" },
  { id: "electrical", label: "Electrical", icon: Zap, description: "Lights, powerpoints, safety & appliances", keywords: "electrician electricians" },
  { id: "flooring", label: "Carpet & Flooring", icon: Layers, description: "Carpet, timber, vinyl & tiles", keywords: "" },
  { id: "painting", label: "Painting", icon: Paintbrush, description: "Interior, exterior, fences & prep", keywords: "painter painters" },
  { id: "handyman", label: "Handyman", icon: Wrench, description: "Assembly, wall mounting & basic repairs", keywords: "" },
  { id: "cleaning", label: "Cleaning", icon: SparklingIcon, description: "End of lease, deep clean & domestic", keywords: "cleaner cleaners" },
  { id: "gardening", label: "Gardening & Yard", icon: Sprout, description: "Mowing, weeding, lopping & gutters", keywords: "gardener landscaper" },
  { id: "tiling", label: "Tiling", icon: Grid, description: "Splashbacks, bathrooms & flooring", keywords: "tiler tilers" },
  { id: "hvac", label: "Air Conditioning", icon: Snowflake, description: "Repairs, servicing & installation", keywords: "aircon air conditioner" },
  { id: "other", label: "Other Trade", icon: Hammer, description: "Custom jobs & generic repairs", keywords: "" },
  ...HIPAGES_CATEGORY_METADATA.map((cat) => ({ ...cat, keywords: "" })),
];

const POPULAR_CATEGORY_IDS = [
  "plumbing",
  "electrical",
  "painting",
  "handyman",
  "cleaning",
  "hvac",
  "roofing",
  "pest_control",
  "bathroom_renovations",
  "kitchen_renovations",
  "locksmiths",
  "tilers",
];

const CATEGORY_QUESTIONS: Record<string, { id: string; text: string; options: string[] }[]> = {
  plumbing: [
    {
      id: "fixture",
      text: "What fixture needs attention?",
      options: ["Toilet", "Tap / Faucet", "Pipes / Leaks", "Shower / Bath", "Hot Water System", "Drains / Blockage"]
    },
    {
      id: "urgency_type",
      text: "Is there active flooding or an urgent leak?",
      options: ["Yes, active flooding", "Slow drip / Minor leak", "No leak, just upgrade/repair", "Not sure"]
    },
    {
      id: "supply_access",
      text: "Is the plumbing line supply easily accessible?",
      options: ["Yes, exposed pipes", "No, behind vanity / wall", "Outdoor mainline", "Not sure"]
    },
    {
      id: "pipe_material",
      text: "What material are the existing pipes?",
      options: ["Copper", "PEX / Plastic", "PVC", "Galvanized Iron", "Not sure"]
    },
    {
      id: "property",
      text: "Property Type",
      options: ["Residential House", "Apartment / Unit", "Commercial Office", "Other"]
    }
  ],
  electrical: [
    {
      id: "service",
      text: "What needs electrical work?",
      options: ["Lights / Switches", "Power Sockets", "Switchboard / Fuse Box", "Ceiling Fan", "Appliance Installation", "Wiring / Sparking"]
    },
    {
      id: "safety",
      text: "Is there any safety hazard (smoke/sparks)?",
      options: ["Yes, active sparking/smoke", "Tripping switches", "No hazard, standard job", "Not sure"]
    },
    {
      id: "materials_supplied",
      text: "Are the electrical components already purchased?",
      options: ["Yes, I have them ready", "No, contractor must supply them", "No parts needed (labor only)"]
    },
    {
      id: "power_status",
      text: "Is the power currently fully out?",
      options: ["Yes, completely out", "Partially out (some rooms)", "No power issue, standard repair"]
    },
    {
      id: "property",
      text: "Property Type",
      options: ["Residential House", "Apartment / Unit", "Commercial Office", "Other"]
    }
  ],
  flooring: [
    {
      id: "location",
      text: "Where is the flooring/carpet needed?",
      options: ["Inside House / Rooms", "Outdoor / Rooftop", "Commercial Office / Shop", "Industrial / Warehouse"]
    },
    {
      id: "material",
      text: "What type of material are we dealing with?",
      options: ["Carpet", "Timber / Hardwood", "Laminate / Vinyl", "Tiles", "Concrete / Epoxy"]
    },
    {
      id: "scope",
      text: "What is the scope of work?",
      options: ["Full Installation (New)", "Repair / Stretching", "Professional Deep Cleaning", "Removal & Disposal"]
    },
    {
      id: "materials_supplied",
      text: "Are the floor materials already supplied?",
      options: ["Yes, materials on-site", "No, contractor must source", "Just need a quote for now"]
    },
    {
      id: "room_size",
      text: "What is the approximate size of the area?",
      options: ["Small (up to 15 sqm)", "Medium (15-30 sqm)", "Large (30+ sqm)", "Not sure"]
    }
  ],
  painting: [
    {
      id: "area",
      text: "What areas need painting?",
      options: ["Interior (Rooms/Ceilings)", "Exterior (Walls/Fences)", "Decks / Pergolas", "Single Wall / Touch-up"]
    },
    {
      id: "prep",
      text: "Do the surfaces need prep work (peeling/mold)?",
      options: ["Yes, significant prep needed", "Minor scraping/sanding", "No, surfaces are ready", "Not sure"]
    },
    {
      id: "paint_supplied",
      text: "Is the paint already supplied?",
      options: ["Yes, I have the paint", "No, contractor must supply", "Just need advice/quotes"]
    },
    {
      id: "height",
      text: "What is the ceiling height?",
      options: ["Standard (2.4m - 2.7m)", "High ceilings (3.0m+)", "Double storey void", "Not sure"]
    },
    {
      id: "coats",
      text: "How many coats of paint are expected?",
      options: ["Standard 2 coats", "Single touch-up coat", "Full primer + 2 coats", "Not sure"]
    }
  ],
  handyman: [
    {
      id: "task",
      text: "What type of handyman service is needed?",
      options: ["Furniture Assembly", "Door / Window Repair", "Wall Mounting (TV/Shelves)", "Cabinet / Joinery Repair", "Deck / Fence Repair", "General Maintenance"]
    },
    {
      id: "materials",
      text: "Are the materials / items purchased?",
      options: ["Yes, I have them on-site", "No, contractor should buy them", "No materials needed (just labor)"]
    },
    {
      id: "scale",
      text: "What is the scale of the task?",
      options: ["Small (Under 1 hour)", "Medium (Half day)", "Large (Full day or more)"]
    },
    {
      id: "tools",
      text: "Are any specialized tools required?",
      options: ["Standard hand tools", "Tall ladder needed", "Specialized power tools", "Not sure"]
    },
    {
      id: "environment",
      text: "Is the job location indoor or outdoor?",
      options: ["Indoor only", "Outdoor only", "Both indoor and outdoor"]
    }
  ],
  cleaning: [
    {
      id: "type",
      text: "What type of cleaning do you need?",
      options: ["End of Lease (Bond Clean)", "Deep Spring Clean", "Regular Domestic Clean", "Commercial / Office Clean", "After Builders / Post-Renovation"]
    },
    {
      id: "size",
      text: "Size of the property?",
      options: ["1 Bedroom Studio", "2-3 Bedroom Home", "4+ Bedroom Large Home", "Small Office", "Large Commercial Space"]
    },
    {
      id: "extras",
      text: "Any additional services required?",
      options: ["Window Cleaning", "Oven Deep Clean", "Carpet Steam Cleaning", "None, standard clean only"]
    },
    {
      id: "supplies_provided",
      text: "Are cleaning supplies and equipment provided?",
      options: ["Yes, use my supplies", "No, contractor must bring all", "Just need basic sweep"]
    },
    {
      id: "pets_stains",
      text: "Are there pets or heavy stains to handle?",
      options: ["Yes, pet hair / heavy stains", "No, standard cleaning", "Not sure"]
    }
  ],
  gardening: [
    {
      id: "service",
      text: "What gardening service is required?",
      options: ["Lawn Mowing & Edging", "Weeding & Garden Cleanup", "Tree Lopping / Hedge Trimming", "Landscaping / Retaining Walls", "Gutter Cleaning"]
    },
    {
      id: "waste",
      text: "Is green waste removal required?",
      options: ["Yes, dispose of all waste", "No, put in my green bin", "No waste will be generated"]
    },
    {
      id: "access",
      text: "Is there easy access to the yard?",
      options: ["Yes, wide gate access", "Narrow access / Stairs", "No gate (must go through house)"]
    },
    {
      id: "yard_size",
      text: "What is the approximate size of the yard?",
      options: ["Small courtyard / balcony", "Medium backyard", "Large block / acreage", "Not sure"]
    },
    {
      id: "last_maintained",
      text: "When was the garden last maintained?",
      options: ["Within 2 weeks", "1-2 months ago", "Over 3 months ago (overgrown)"]
    }
  ],
  tiling: [
    {
      id: "location",
      text: "Where is the tiling located?",
      options: ["Bathroom / Shower", "Kitchen Splashback", "Main Floor Area", "Outdoor Patio / Balcony", "Pool Area"]
    },
    {
      id: "prep",
      text: "Are the old tiles already removed?",
      options: ["Yes, surface is bare and ready", "No, contractor needs to remove old tiles", "Brand new build (no removal needed)", "Not sure"]
    },
    {
      id: "tiles_supplied",
      text: "Are the tiles already purchased?",
      options: ["Yes, tiles are ready on-site", "No, I need the contractor to source them", "Not yet, need suggestions"]
    },
    {
      id: "tile_type",
      text: "What type of tile material are you using?",
      options: ["Ceramic", "Porcelain", "Marble / Natural Stone", "Mosaic", "Subway / Splashback tiles"]
    },
    {
      id: "waterproofing",
      text: "Is waterproofing required before tiling?",
      options: ["Yes, needs full waterproofing", "No, already done", "Not sure"]
    }
  ],
  hvac: [
    {
      id: "issue",
      text: "What is the primary issue with the AC?",
      options: ["Not cooling / heating", "Leaking water", "Strange noise / Odor", "No power / dead unit", "Routine Service / Clean", "New Installation"]
    },
    {
      id: "system",
      text: "What type of system is it?",
      options: ["Split System (Wall Mounted)", "Ducted System", "Multi-split System", "Window AC", "Not sure"]
    },
    {
      id: "brand",
      text: "System Brand?",
      options: ["Daikin / Panasonic / Mitsubishi", "Samsung / LG / Fujitsu", "Generic / Other", "Not sure"]
    },
    {
      id: "unit_location",
      text: "Where is the outdoor unit located?",
      options: ["Ground level (easy access)", "Balcony", "Rooftop (ladder/harness required)", "Not sure"]
    },
    {
      id: "residential_commercial",
      text: "Is this a residential or commercial space?",
      options: ["Residential Home / Apartment", "Commercial Office / Shop", "Industrial Space"]
    }
  ],
  other: [
    {
      id: "environment",
      text: "What best describes the environment?",
      options: ["Residential House", "Commercial Office / Retail", "Outdoor Area", "Industrial Site"]
    },
    {
      id: "scale",
      text: "What is the scale of the task?",
      options: ["Small (quick fix)", "Medium (half day repair)", "Large (complex installation)"]
    },
    {
      id: "materials",
      text: "Are materials supplied?",
      options: ["Yes", "No", "Not applicable"]
    },
    {
      id: "emergency",
      text: "Is there active damage or an emergency?",
      options: ["Yes, active emergency", "No, planned maintenance", "Not sure"]
    },
    {
      id: "safety_hazards",
      text: "Are there any safety hazards involved?",
      options: ["Yes", "No", "Not sure"]
    }
  ]
};

const STEPS = [
  { label: "Category", desc: "Select trade" },
  { label: "Questions", desc: "Job specifics" },
  { label: "Details", desc: "Describe issue" },
  { label: "Media", desc: "Attach photos" },
  { label: "Location", desc: "Choose address" },
  { label: "Estimate", desc: "AI Lock rate" }
];

function BookingContent() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const [category, setCategory] = useState("");
  const [categorySearch, setCategorySearch] = useState("");
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const [customTextAnswer, setCustomTextAnswer] = useState("");
  const [currentStep, setCurrentStep] = useState(0);
  const [subQuestionIndex, setSubQuestionIndex] = useState(0);

  const [description, setDescription] = useState("");
  const autoDescriptionRef = useRef("");
  const [location, setLocation] = useState("");
  const [address, setAddress] = useState("");
  const [postcode, setPostcode] = useState("");
  const [urgency, setUrgency] = useState("today");
  const [estimate, setEstimate] = useState<PricingEstimate | null>(null);
  const [loadingEstimate, setLoadingEstimate] = useState(false);
  const [loadingBook, setLoadingBook] = useState(false);
  const [booked, setBooked] = useState<Job | null>(null);
  const [error, setError] = useState("");
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [mediaUrls, setMediaUrls] = useState<string[]>([]);
  const [customerLocation, setCustomerLocation] = useState<any>(null);

  useEffect(() => {
    const session = getSession();
    if (session?.user) setIsLoggedIn(true);
    
    // Pre-fill from query params if any
    const categoryParam = searchParams.get("category");
    if (categoryParam && CATEGORY_METADATA.some(c => c.id === categoryParam)) {
      setCategory(categoryParam);
      setCurrentStep(1);
    } else {
      const desc = searchParams.get("description");
      if (desc) {
        setDescription(desc);
        setCategory("other");
        setCurrentStep(1);
      }
    }

    // Job continuity logic
    if (session?.user) {
      const pending = localStorage.getItem("pending_job");
      if (pending) {
        const data = JSON.parse(pending);
        localStorage.removeItem("pending_job");
        
        // Auto-post the job
        (async () => {
          setLoadingBook(true);
          try {
            const job = await api.createJob(data);
            setBooked(job as Job);
          } catch (e: any) {
            setError(e.message || "Failed to post pending job");
          } finally {
            setLoadingBook(false);
          }
        })();
      }
    }
  }, [searchParams]);

  // Sync custom input field with currently stored answer when step/sub-question changes
  useEffect(() => {
    if (category) {
      const qList = CATEGORY_QUESTIONS[category] || CATEGORY_QUESTIONS.other;
      const currentQ = qList[subQuestionIndex];
      if (currentQ) {
        const currentAns = answers[currentQ.id] || "";
        const isPreset = currentQ.options.includes(currentAns);
        setCustomTextAnswer(isPreset ? "" : currentAns);
      }
    }
  }, [subQuestionIndex, category, answers]);

  function buildAutoDescription(answersOverride?: Record<string, string>) {
    const activeAnswers = answersOverride || answers;
    const selectedCat = CATEGORY_METADATA.find(c => c.id === category)?.label || category;
    const questions = CATEGORY_QUESTIONS[category] || CATEGORY_QUESTIONS.other;
    const parts = questions
      .filter(q => activeAnswers[q.id])
      .map(q => `${q.text.replace(/\?$/, "")}: ${activeAnswers[q.id]}`);
    return [`${selectedCat} issue.`, ...parts.map(p => `${p}.`)].join(" ");
  }

  // Shared by both ways a question can be answered (clicking a preset option,
  // or typing a custom answer + "Next Question"): advances to the next
  // sub-question, or - on the last one - auto-fills the description from the
  // full answer set (including the answer just given, which hasn't landed in
  // React state yet) and moves to the Details step.
  function advanceQuestionStep(justAnsweredId?: string, justAnsweredValue?: string) {
    const questions = CATEGORY_QUESTIONS[category] || CATEGORY_QUESTIONS.other;
    if (subQuestionIndex < questions.length - 1) {
      setSubQuestionIndex(prev => prev + 1);
      return;
    }
    const mergedAnswers = justAnsweredId ? { ...answers, [justAnsweredId]: justAnsweredValue || "" } : answers;
    if (!description.trim() || description === autoDescriptionRef.current) {
      const generated = buildAutoDescription(mergedAnswers);
      autoDescriptionRef.current = generated;
      setDescription(generated);
    }
    setCurrentStep(2);
  }

  function getFullDescription() {
    const selectedCat = CATEGORY_METADATA.find(c => c.id === category)?.label || category;
    let summary = `📋 SPECIFIC DETAILS FOR ${selectedCat.toUpperCase()}:\n`;
    const questions = CATEGORY_QUESTIONS[category] || CATEGORY_QUESTIONS.other;
    
    questions.forEach(q => {
      const ans = answers[q.id];
      if (ans) {
        summary += `• ${q.text}: ${ans}\n`;
      }
    });

    summary += `\n✍️ DESCRIPTION:\n${description}`;
    return summary;
  }

  async function handleEstimate() {
    if (!description.trim()) { setError("Please describe your issue first"); return; }
    setError("");
    setLoadingEstimate(true);
    setBooked(null);
    try {
      const result = await api.estimatePricing({ 
        description: getFullDescription(), 
        location: location || address || "Global", 
        postcode, 
        urgency 
      });
      setEstimate(result as PricingEstimate);
      setCurrentStep(5);
    } catch {
      setError("Could not get estimate — is the backend running?");
    } finally { setLoadingEstimate(false); }
  }

  const handleLogout = () => {
    clearSession();
    setIsLoggedIn(false);
    router.push("/");
  };

  async function handleBook() {
    const session = getSession();
    const images = mediaUrls.filter(url => !url.endsWith(".mp4") && !url.includes("video") && !url.includes("ForBiggerBlazes"));
    const videos = mediaUrls.filter(url => url.endsWith(".mp4") || url.includes("video") || url.includes("ForBiggerBlazes"));

    const finalDesc = getFullDescription();

    if (!session || session.role !== "customer" || !session.token) {
      // Save for continuity
      localStorage.setItem("pending_job", JSON.stringify({
        description: finalDesc,
        location: location || address || "Global",
        address,
        postcode,
        urgency,
        customerLocation,
        images,
        videos
      }));
      router.push(`/auth-choice?redirect=/booking`);
      return;
    }
    setLoadingBook(true);
    setError("");
    try {
      const job = await api.createJob({ 
        description: finalDesc, 
        location: location || address || "Global", 
        address, 
        postcode, 
        urgency,
        customerLocation,
        images,
        videos
      });
      setBooked(job as Job);

    } catch (e: any) {
      setError(e.message || "Could not post job");
    } finally { setLoadingBook(false); }
  }

  if (booked) {
    return (
      <div className="max-w-2xl mx-auto py-24 px-6 animate-fade-in">
        <div className="text-center mb-12">
          <div className="mx-auto mb-6 flex h-20 w-20 items-center justify-center rounded-full bg-emerald-100 ring-8 ring-emerald-50">
            <CheckCircle2 className="h-10 w-10 text-emerald-600" />
          </div>
          <h1 className="text-3xl font-bold text-slate-950">Job Successfully Posted!</h1>
          <p className="mt-3 text-slate-500">
            {booked.contractor ? `${booked.contractor.name} has been assigned.` : "We're matching you with a verified contractor now."}
          </p>
        </div>

        <div className="rounded-3xl border border-slate-200 bg-white overflow-hidden shadow-xl">
          <div className="p-8 border-b border-slate-100 bg-slate-50/50">
            <h2 className="text-sm font-bold uppercase tracking-widest text-slate-400 mb-6">Booking Details</h2>
            <div className="mb-6">
              <JobDetails description={booked.description} />
            </div>
            <div className="space-y-4">
              {[
                // ["Issue", booked.description],
                ["Full Address", booked.address || "N/A"],
                ["Postcode", booked.postcode || "N/A"],
                ["Location", booked.location],
                ["Urgency", booked.urgency],
                ["Category", booked.category],
                ["Estimate Range", formatEstimateRange(booked)],
                ["Status", booked.status.replace(/_/g, " ")]
              ].map(([k, v]) => (
                <div key={k} className="flex justify-between items-start gap-4">
                  <span className="text-sm font-medium text-slate-500 shrink-0">{k}</span>
                  <span className="text-sm font-bold text-slate-950 text-right capitalize whitespace-pre-line">{v}</span>
                </div>
              ))}
            </div>
          </div>

          {booked.contractor && (
            <div className="p-8 bg-emerald-50/30">
              <div className="flex items-center justify-between gap-4">
                <div className="flex items-center gap-4">
                  <div className="h-12 w-12 rounded-2xl bg-emerald-100 flex items-center justify-center text-emerald-700 font-bold">
                    {booked.contractor.name.charAt(0)}
                  </div>
                  <div>
                    <p className="font-bold text-slate-950">{booked.contractor.name}</p>
                    <p className="text-xs text-slate-500">{booked.contractor.trade.split(",")[0]} · ⭐ {booked.contractor.rating || "New"}</p>
                  </div>
                </div>
                <a href={`tel:${booked.contractor.phones?.[0]?.number || ""}`} className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-emerald-600 text-white font-bold text-sm shadow-lg shadow-emerald-200 hover:bg-emerald-700 transition-all">
                  <Phone size={16} />
                  Call
                </a>
              </div>
            </div>
          )}
        </div>

        <div className="mt-8 flex flex-col gap-3">
          <Link href="/dashboard" className="w-full flex items-center justify-center gap-2 py-4 rounded-2xl bg-emerald-600 text-white font-bold hover:bg-slate-800 transition-all">
            Track in Dashboard
            <ChevronRight size={18} />
          </Link>
          <button 
            onClick={() => { setBooked(null); setEstimate(null); setDescription(""); setAnswers({}); setCategory(""); setCurrentStep(0); }}
            className="w-full py-4 rounded-2xl border border-slate-200 text-slate-600 font-bold hover:bg-slate-50 transition-all"
          >
            Post Another Job
          </button>
        </div>
      </div>
    );
  }

  const questions = CATEGORY_QUESTIONS[category] || CATEGORY_QUESTIONS.other;
  const categoryTokens = categorySearch.trim().toLowerCase().split(/\s+/).filter((t) => t.length >= 2);
  const filteredCategories = categoryTokens.length
    ? CATEGORY_METADATA
        .map((cat) => {
          const text = `${cat.label} ${cat.description} ${cat.id} ${cat.keywords}`.toLowerCase();
          const score = categoryTokens.reduce((acc, t) => acc + (text.includes(t) ? 1 : 0), 0);
          return { cat, score };
        })
        .filter((x) => x.score > 0)
        .sort((a, b) => b.score - a.score)
        .map((x) => x.cat)
    : CATEGORY_METADATA;
  const popularCategories = POPULAR_CATEGORY_IDS
    .map((id) => CATEGORY_METADATA.find((cat) => cat.id === id))
    .filter(Boolean) as typeof CATEGORY_METADATA;

  const selectCategory = (id: string) => {
    setCategory(id);
    setAnswers({});
    setSubQuestionIndex(0);
    setCurrentStep(1);
  };

  return (
    <div className="max-w-6xl mx-auto pt-32 pb-24 px-6">
      {/* Dynamic Animated Stepper */}
      <div className="mb-14 max-w-4xl mx-auto">
        <div className="flex justify-between items-center relative">
          <div className="absolute top-1/2 left-0 right-0 h-0.5 bg-slate-100 -translate-y-1/2 z-0" />
          <div 
            className="absolute top-1/2 left-0 h-0.5 bg-emerald-500 -translate-y-1/2 z-0 transition-all duration-500" 
            style={{ width: `${(currentStep / (STEPS.length - 1)) * 100}%` }}
          />
          {STEPS.map((s, idx) => {
            const isActive = idx === currentStep;
            const isCompleted = idx < currentStep;
            return (
              <div key={idx} className="flex flex-col items-center z-10 relative">
                <div 
                  className={`h-10 w-10 rounded-full flex items-center justify-center font-bold text-xs transition-all duration-300 border-2 ${
                    isActive 
                      ? "bg-slate-900 border-slate-900 text-white scale-110 shadow-lg shadow-slate-200" 
                      : isCompleted 
                      ? "bg-emerald-500 border-emerald-500 text-white" 
                      : "bg-white border-slate-200 text-slate-400"
                  }`}
                >
                  {isCompleted ? "✓" : idx + 1}
                </div>
                <span className={`text-[10px] font-bold mt-2 uppercase tracking-wider hidden sm:block ${isActive ? "text-slate-950 font-extrabold" : "text-slate-400"}`}>
                  {s.label}
                </span>
              </div>
            );
          })}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 items-start">
        {/* Left Interactive Wizard Column */}
        <div className="lg:col-span-7">
          <AnimatePresence mode="wait">
            {currentStep === 0 && (
              <motion.div
                key="step0"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 20 }}
                className="space-y-6"
              >
                <div>
                  <button 
                    onClick={() => router.push("/")}
                    className="flex items-center gap-2 text-slate-400 hover:text-slate-600 mb-6 transition-colors"
                  >
                    <ArrowLeft size={16} />
                    <span className="text-sm font-bold">Back to Home</span>
                  </button>
                  <div className="inline-flex items-center gap-2 rounded-full border border-emerald-100 bg-emerald-50 px-3 py-1 text-[11px] font-black uppercase tracking-widest text-emerald-700 mb-4">
                    <ShieldCheck size={14} />
                    Vetted local service directory
                  </div>
                  <h1 className="text-4xl md:text-5xl font-black text-slate-950 tracking-tight">What needs fixing?</h1>
                  <p className="text-slate-600 mt-3 max-w-2xl text-base leading-relaxed">
                    Search or select a trade to start a guided booking. We will collect the right details, attach photos, and prepare a clear upfront estimate.
                  </p>
                </div>

                <div className="rounded-3xl border border-slate-200 bg-white p-3 shadow-xl shadow-slate-200/60">
                  <label className="flex items-center gap-3 rounded-2xl bg-slate-50 px-4 py-4 border border-slate-100">
                    <Search size={20} className="text-slate-400" />
                    <input
                      value={categorySearch}
                      onChange={(event) => setCategorySearch(event.target.value)}
                      placeholder="Search 200+ services, e.g. plumber, pest control, roof repairs"
                      className="w-full bg-transparent text-sm font-bold text-slate-900 placeholder:text-slate-400 focus:outline-none"
                    />
                  </label>
                </div>

                {!categorySearch && (
                  <div className="rounded-3xl bg-slate-950 p-5 text-white">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-4">
                      <div>
                        <p className="text-xs font-black uppercase tracking-widest text-emerald-300">Popular services</p>
                        <p className="text-sm text-slate-300 mt-1">Start with the trades customers request most often.</p>
                      </div>
                      <span className="text-xs font-bold text-slate-400">{CATEGORY_METADATA.length} services available</span>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {popularCategories.map((cat) => (
                        <button
                          key={cat.id}
                          onClick={() => selectCategory(cat.id)}
                          className="rounded-full bg-white/10 px-4 py-2 text-sm font-bold text-white hover:bg-emerald-500 transition-colors"
                        >
                          {cat.label}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                <div className="flex items-center justify-between gap-4">
                  <p className="text-xs font-black uppercase tracking-widest text-slate-400">
                    {filteredCategories.length} matching services
                  </p>
                  {categorySearch && (
                    <button
                      onClick={() => setCategorySearch("")}
                      className="text-xs font-bold text-emerald-600 hover:text-emerald-700"
                    >
                      Clear search
                    </button>
                  )}
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {filteredCategories.map((cat) => {
                    const IconComponent = cat.icon;
                    return (
                      <button
                        key={cat.id}
                        onClick={() => selectCategory(cat.id)}
                        className="flex items-center gap-4 p-4 rounded-2xl border border-slate-200 bg-white text-left transition-all hover:border-emerald-500 hover:shadow-lg hover:shadow-emerald-500/5 group active:scale-[0.98]"
                      >
                        <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-slate-50 text-slate-600 group-hover:bg-emerald-50 group-hover:text-emerald-600 transition-colors">
                          <IconComponent size={23} strokeWidth={1.7} />
                        </span>
                        <span className="min-w-0">
                          <span className="block font-black text-slate-900 group-hover:text-emerald-600 transition-colors leading-tight">{cat.label}</span>
                          <span className="block text-xs text-slate-400 mt-1 leading-snug">{cat.description}</span>
                        </span>
                      </button>
                    );
                  })}
                </div>
              </motion.div>
            )}

            {currentStep === 1 && questions && questions[subQuestionIndex] && (
              <motion.div
                key={`step1-${subQuestionIndex}`}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 20 }}
                className="space-y-8"
              >
                <div>
                  <button 
                    onClick={() => {
                      if (subQuestionIndex > 0) {
                        setSubQuestionIndex(prev => prev - 1);
                      } else {
                        setCurrentStep(0);
                      }
                    }}
                    className="flex items-center gap-2 text-slate-400 hover:text-slate-600 mb-6 transition-colors"
                  >
                    <ArrowLeft size={16} />
                    <span className="text-sm font-bold">Back</span>
                  </button>
                  <span className="text-xs font-bold uppercase tracking-widest text-emerald-600">Question {subQuestionIndex + 1} of {questions.length}</span>
                  <h2 className="text-2xl font-bold text-slate-950 mt-1">{questions[subQuestionIndex].text}</h2>
                </div>

                <div className="space-y-3">
                  {questions[subQuestionIndex].options.map((opt) => {
                    const isSelected = answers[questions[subQuestionIndex].id] === opt;
                    return (
                      <button
                        key={opt}
                        onClick={() => {
                          const questionId = questions[subQuestionIndex].id;
                          setAnswers(prev => ({ ...prev, [questionId]: opt }));
                          setCustomTextAnswer("");
                          advanceQuestionStep(questionId, opt);
                        }}
                        className={`w-full flex items-center justify-between p-5 rounded-2xl border text-left font-bold transition-all active:scale-[0.99] ${
                          isSelected 
                            ? "border-emerald-500 bg-emerald-50/50 text-emerald-950 ring-4 ring-emerald-50" 
                            : "border-slate-200 bg-white text-slate-700 hover:border-slate-300 hover:bg-slate-50/30"
                        }`}
                      >
                        <span>{opt}</span>
                        <div className={`h-5 w-5 rounded-full border flex items-center justify-center transition-all ${
                          isSelected ? "border-emerald-500 bg-emerald-500 text-white" : "border-slate-300"
                        }`}>
                          {isSelected && <div className="h-2 w-2 rounded-full bg-white" />}
                        </div>
                      </button>
                    );
                  })}

                  {/* Flexible Custom Answer Input */}
                  <div className="pt-5 border-t border-slate-100 mt-6 space-y-2.5">
                    <div className="flex items-center gap-2 text-slate-400">
                      <HelpCircle size={14} className="text-slate-400" />
                      <span className="text-xs font-bold uppercase tracking-widest">Or type your specific issue / custom detail</span>
                    </div>
                    <input
                      type="text"
                      placeholder="Type custom details here..."
                      value={customTextAnswer}
                      onChange={(e) => {
                        const val = e.target.value;
                        setCustomTextAnswer(val);
                        if (val.trim()) {
                          setAnswers(prev => ({ ...prev, [questions[subQuestionIndex].id]: val }));
                        } else {
                          // Clean up if cleared
                          setAnswers(prev => {
                            const clone = { ...prev };
                            delete clone[questions[subQuestionIndex].id];
                            return clone;
                          });
                        }
                      }}
                      className="w-full h-14 rounded-2xl border border-slate-200 px-5 text-sm text-slate-900 outline-none focus:border-emerald-500 focus:ring-4 focus:ring-emerald-50 transition-all bg-white shadow-sm"
                    />
                  </div>
                </div>

                {error && (
                  <div className="p-4 rounded-xl bg-rose-50 text-rose-700 text-sm font-semibold">{error}</div>
                )}

                <div className="flex justify-between pt-6 border-t border-slate-100">
                  <button
                    onClick={() => {
                      if (subQuestionIndex > 0) {
                        setSubQuestionIndex(prev => prev - 1);
                      } else {
                        setCurrentStep(0);
                      }
                    }}
                    className="px-6 py-3 rounded-xl border border-slate-200 text-slate-600 font-bold hover:bg-slate-50 transition-all flex items-center gap-2 text-sm"
                  >
                    <ArrowLeft size={16} />
                    Back
                  </button>

                  <button
                    onClick={() => {
                      const currentQ = questions[subQuestionIndex];
                      if (!answers[currentQ.id]) {
                        setError("Please select an option or type a custom answer.");
                        return;
                      }
                      setError("");
                      advanceQuestionStep();
                    }}
                    className="px-6 py-3 rounded-xl bg-emerald-600 text-white font-bold hover:bg-slate-800 transition-all flex items-center gap-2 text-sm"
                  >
                    Next Question
                    <ChevronRight size={16} />
                  </button>
                </div>
              </motion.div>
            )}

            {currentStep === 2 && (
              <motion.div
                key="step2"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 20 }}
                className="space-y-6"
              >
                <div>
                  <button 
                    onClick={() => {
                      setSubQuestionIndex(questions.length - 1);
                      setCurrentStep(1);
                    }}
                    className="flex items-center gap-2 text-slate-400 hover:text-slate-600 mb-6 transition-colors"
                  >
                    <ArrowLeft size={16} />
                    <span className="text-sm font-bold">Back</span>
                  </button>
                  <h2 className="text-2xl font-bold text-slate-950">Describe the issue</h2>
                  <p className="text-sm text-slate-400 mt-1">Please provide standard details or symptom explanations so our pricing model can assess clearly.</p>
                </div>

                <textarea
                  value={description}
                  onChange={e => setDescription(e.target.value)}
                  placeholder="e.g. Faucet slow drips that won't stop. Need replacement of valve washer..."
                  rows={5}
                  className="w-full rounded-2xl border border-slate-200 px-5 py-4 text-slate-900 outline-none focus:border-emerald-500 focus:ring-4 focus:ring-emerald-50 transition-all resize-none shadow-sm"
                />

                {error && (
                  <div className="p-4 rounded-xl bg-rose-50 text-rose-700 text-sm font-semibold">{error}</div>
                )}

                <div className="flex justify-between pt-4">
                  <button
                    onClick={() => {
                      setSubQuestionIndex(questions.length - 1);
                      setCurrentStep(1);
                    }}
                    className="px-6 py-3 rounded-xl border border-slate-200 text-slate-600 font-bold hover:bg-slate-50 transition-all flex items-center gap-2 text-sm"
                  >
                    <ArrowLeft size={16} />
                    Back
                  </button>
                  <button
                    onClick={() => {
                      if (!description.trim()) {
                        setError("Please explain the issue details.");
                        return;
                      }
                      setError("");
                      setCurrentStep(3);
                    }}
                    className="px-6 py-3 rounded-xl bg-emerald-600 text-white font-bold hover:bg-slate-800 transition-all flex items-center gap-2 text-sm"
                  >
                    Next: Photos & Videos
                    <ChevronRight size={16} />
                  </button>
                </div>
              </motion.div>
            )}

            {currentStep === 3 && (
              <motion.div
                key="step3"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 20 }}
                className="space-y-6"
              >
                <div>
                  <button 
                    onClick={() => setCurrentStep(2)}
                    className="flex items-center gap-2 text-slate-400 hover:text-slate-600 mb-6 transition-colors"
                  >
                    <ArrowLeft size={16} />
                    <span className="text-sm font-bold">Back</span>
                  </button>
                  <h2 className="text-2xl font-bold text-slate-950">Attach Photos or Videos</h2>
                  <p className="text-sm text-slate-400 mt-1">Highly recommended so contractors get complete clarity before arriving.</p>
                </div>

                <MediaUploader
                  label="Select files to show contractors the problem"
                  onUploadComplete={(urls) => setMediaUrls(urls)}
                  maxFiles={6}
                />

                <div className="flex justify-between pt-4">
                  <button
                    onClick={() => setCurrentStep(2)}
                    className="px-6 py-3 rounded-xl border border-slate-200 text-slate-600 font-bold hover:bg-slate-50 transition-all flex items-center gap-2 text-sm"
                  >
                    <ArrowLeft size={16} />
                    Back
                  </button>
                  <button
                    onClick={() => setCurrentStep(4)}
                    className="px-6 py-3 rounded-xl bg-emerald-600 text-white font-bold hover:bg-slate-800 transition-all flex items-center gap-2 text-sm"
                  >
                    Next: Destination
                    <ChevronRight size={16} />
                  </button>
                </div>
              </motion.div>
            )}

            {currentStep === 4 && (
              <motion.div
                key="step4"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 20 }}
                className="space-y-6"
              >
                <div>
                  <button 
                    onClick={() => setCurrentStep(3)}
                    className="flex items-center gap-2 text-slate-400 hover:text-slate-600 mb-6 transition-colors"
                  >
                    <ArrowLeft size={16} />
                    <span className="text-sm font-bold">Back</span>
                  </button>
                  <h2 className="text-2xl font-bold text-slate-950">Service Address & Timing</h2>
                  <p className="text-sm text-slate-400 mt-1">Provide exact destination coordinates and project urgency details.</p>
                </div>

                <div className="space-y-4">
                  <div className="space-y-2">
                    <label className="text-xs font-bold text-slate-950 uppercase tracking-widest flex items-center gap-2">
                      <MapPin size={12} />
                      Service Location Map Search
                    </label>
                    <DestinationPicker 
                      initialAddress={address}
                      onConfirm={(loc) => {
                        setCustomerLocation(loc);
                        setAddress(loc.address);
                        setPostcode(loc.postcode || "");
                        const cityState = [loc.city, loc.state].filter(Boolean).join(", ");
                        setLocation(cityState || loc.country || "Unknown Location");
                      }} 
                    />
                  </div>

                  <div className="space-y-2">
                    <label className="text-xs font-bold text-slate-950 uppercase tracking-widest flex items-center gap-2">
                      <Clock size={12} />
                      Timing / Urgency
                    </label>
                    <div className="relative">
                      <select
                        value={urgency}
                        onChange={e => setUrgency(e.target.value)}
                        className="w-full h-14 rounded-2xl border border-slate-200 px-5 text-slate-900 outline-none focus:border-emerald-500 transition-all appearance-none cursor-pointer bg-white shadow-sm"
                      >
                        {URGENCY_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label} — {o.sub}</option>)}
                      </select>
                      <ChevronRight size={18} className="absolute right-5 top-1/2 -translate-y-1/2 rotate-90 text-slate-400 pointer-events-none" />
                    </div>
                  </div>
                </div>

                {error && (
                  <div className="p-4 rounded-xl bg-rose-50 text-rose-700 text-sm font-semibold">{error}</div>
                )}

                <div className="flex justify-between pt-4">
                  <button
                    onClick={() => setCurrentStep(3)}
                    className="px-6 py-3 rounded-xl border border-slate-200 text-slate-600 font-bold hover:bg-slate-50 transition-all flex items-center gap-2 text-sm"
                  >
                    <ArrowLeft size={16} />
                    Back
                  </button>
                  <button
                    onClick={handleEstimate}
                    disabled={loadingEstimate || !description.trim() || !address}
                    className="px-6 py-3 rounded-xl bg-emerald-600 text-white font-bold hover:bg-emerald-700 disabled:opacity-50 transition-all flex items-center justify-center gap-2 text-sm shadow-xl shadow-emerald-100"
                  >
                    {loadingEstimate ? (
                      <div className="h-5 w-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    ) : (
                      <>
                        Calculate Estimate
                        <ChevronRight size={16} />
                      </>
                    )}
                  </button>
                </div>
              </motion.div>
            )}

            {currentStep === 5 && estimate && (
              <motion.div
                key="step5"
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                className="rounded-3xl border border-emerald-200 bg-gradient-to-br from-emerald-50/50 to-white p-8 shadow-2xl shadow-emerald-900/5 relative overflow-hidden"
              >
                <div className="absolute top-0 right-0 p-6">
                   <ShieldCheck className="text-emerald-200 h-16 w-16 -mr-4 -mt-4 opacity-50" />
                </div>

                <p className="text-xs font-bold text-emerald-600 uppercase tracking-widest mb-2 flex items-center gap-2">
                  <Sparkles size={14} />
                  Estimated Market Range
                </p>
                
                <div className="flex flex-col gap-2 mb-6">
                  <span className="text-5xl font-bold text-slate-950">{formatEstimateRange(estimate)}</span>
                  <span className="text-slate-400 text-sm font-medium">Final price is confirmed after contractor bids, scope, and approved parts.</span>
                </div>

                <div className="space-y-4 mb-8">
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-500">Service Category</span>
                    <span className="font-bold text-slate-900 capitalize">{estimate.category}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-500">Confidence Score</span>
                    <span className={`font-bold ${estimate.confidence === 'high' ? 'text-emerald-600' : 'text-amber-600'}`}>{estimate.confidence}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-500">Market Range</span>
                    <span className="font-bold text-slate-900">{formatEstimateRange(estimate)}</span>
                  </div>
                </div>

                <div className="p-4 rounded-2xl bg-white border border-emerald-100 flex items-start gap-3 mb-8">
                  <Info size={16} className="text-emerald-600 mt-0.5" />
                  <p className="text-xs leading-relaxed text-slate-600">
                    This is a market range for your request. Verified contractors will submit bids, then you choose the final contractor and price before work starts.
                  </p>
                </div>

                {error && (
                  <div className="p-4 rounded-xl bg-rose-50 text-rose-700 text-sm font-semibold mb-6">{error}</div>
                )}

                <button
                  onClick={handleBook}
                  disabled={loadingBook}
                  className="w-full py-5 rounded-2xl bg-slate-900 text-white font-bold hover:bg-slate-800 transition-all shadow-xl shadow-slate-200 flex items-center justify-center gap-2 active:scale-[0.98]"
                >
                  {loadingBook ? (
                     <div className="h-5 w-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  ) : (
                    <>
                      {isLoggedIn ? "Confirm & Post Now" : "Log in to Confirm Posting"}
                      <ChevronRight size={18} />
                    </>
                  )}
                </button>

                <button
                  onClick={() => {
                    setEstimate(null);
                    setCurrentStep(4);
                  }}
                  className="w-full py-3 rounded-xl border border-slate-200 text-slate-600 font-bold hover:bg-slate-50 transition-all mt-4 text-sm"
                >
                  Back to Details
                </button>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Right Info Preview Column */}
        <div className="lg:col-span-5 space-y-8 lg:sticky lg:top-32">
          {/* Dynamic Real-time Preview Card */}
          <div className="rounded-3xl border border-slate-200 bg-slate-50/50 p-8 space-y-6 shadow-sm">
            <h3 className="text-xs font-bold uppercase tracking-widest text-slate-400">Live Job Preview</h3>
            
            <div className="space-y-4">
              {category ? (
                (() => {
                  const catMeta = CATEGORY_METADATA.find(c => c.id === category);
                  if (!catMeta) return null;
                  const IconComponent = catMeta.icon;
                  return (
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-500">Service Category</span>
                      <span className="font-bold text-slate-950 flex items-center gap-1.5 capitalize">
                        <IconComponent size={16} className="text-emerald-600" />
                        {catMeta.label}
                      </span>
                    </div>
                  );
                })()
              ) : (
                <p className="text-xs text-slate-400 italic">Select a trade category to start building your job preview.</p>
              )}
              
              {category && Object.keys(answers).length > 0 && (
                <div className="space-y-2 pt-3 border-t border-slate-100">
                  <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400 block">Specific Details</span>
                  {(() => {
                    const questionsList = CATEGORY_QUESTIONS[category] || CATEGORY_QUESTIONS.other;
                    return questionsList.map(q => {
                      const ans = answers[q.id];
                      if (!ans) return null;
                      return (
                        <div key={q.id} className="flex justify-between text-xs gap-4">
                          <span className="text-slate-500 text-left">{q.text}</span>
                          <span className="font-bold text-slate-900 text-right shrink-0">{ans}</span>
                        </div>
                      );
                    });
                  })()}
                </div>
              )}

              {description.trim() && (
                <div className="pt-3 border-t border-slate-100">
                  <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400 block mb-1">Standard Description</span>
                  <p className="text-xs text-slate-600 italic line-clamp-3 bg-white p-3 rounded-xl border border-slate-100">"{description}"</p>
                </div>
              )}

              {mediaUrls.length > 0 && (
                <div className="flex justify-between text-sm pt-3 border-t border-slate-100">
                  <span className="text-slate-500">Attachments</span>
                  <span className="font-bold text-emerald-600">{mediaUrls.length} file(s) attached</span>
                </div>
              )}

              {address && (
                <div className="flex justify-between text-sm pt-3 border-t border-slate-100 gap-4">
                  <span className="text-slate-500 shrink-0">Destination</span>
                  <span className="font-bold text-slate-900 text-right text-xs truncate max-w-[200px]" title={address}>{address}</span>
                </div>
              )}

              {urgency && (
                <div className="flex justify-between text-sm pt-3 border-t border-slate-100">
                  <span className="text-slate-500">Timing</span>
                  <span className="font-bold text-slate-900 capitalize">
                    {URGENCY_OPTIONS.find(o => o.value === urgency)?.label}
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Core Platform Benefits */}
          <div className="grid grid-cols-1 gap-4">
            {[
              { title: "Local Trades Near You", icon: <ShieldCheck size={18} className="text-emerald-500" /> },
              { title: "Compare Bids, Pick Your Price", icon: <CreditCard size={18} className="text-emerald-500" /> },
              { title: "Pay Only When It's Done", icon: <CheckCircle2 size={18} className="text-emerald-500" /> }
            ].map((b, i) => (
              <div key={i} className="flex items-center gap-4 p-4 rounded-2xl bg-white border border-slate-100 shadow-sm">
                <div className="h-10 w-10 rounded-xl bg-slate-50 flex items-center justify-center">
                  {b.icon}
                </div>
                <span className="text-sm font-bold text-slate-700">{b.title}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function CreditCard(props: any) {
  return (
    <svg
      {...props}
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <rect width="20" height="14" x="2" y="5" rx="2" />
      <line x1="2" x2="22" y1="10" y2="10" />
    </svg>
  );
}

export default function BookingPage() {
  return (
    <main className="min-h-screen bg-white">
      <Suspense fallback={<div className="pt-32 text-center text-slate-400 animate-pulse">Loading booking system...</div>}>
        <BookingContent />
      </Suspense>
    </main>
  );
}
